import { VAST } from '@lib/iab/iab-vast-model';
import { DOMParser } from 'xmldom';

import Errors from './errors/error-codes';
import ErrorHandler from './errors/error-handler';
import createVAST from './factory/vast';
import { VASTParserOptions } from './index';
import schema from './vast/schema';
import Unmarshaler from './xml/unmarshaler';

interface ParseOptions extends VASTParserOptions {
    strict: boolean;
    noSingleAdPods: boolean;
    errorHandler: ErrorHandler;
}

const DEFAULT_OPTIONS: ParseOptions = {
    strict: false,
    noSingleAdPods: false,
    errorHandler: new ErrorHandler(false),
};

const toElement = (xml: string | Document, options: ParseOptions): Element => {
    let element: Document | Element;

    if (typeof xml === 'string') {
        const domParser = options.domParser || new DOMParser();
        element = domParser.parseFromString(xml, 'text/xml');
    } else {
        element = xml;
    }

    // If it's a Document, get the documentElement
    if ('documentElement' in element && element.documentElement != null) {
        element = element.documentElement;
    }

    if (element.getElementsByTagName('parsererror').length > 0) {
        options.errorHandler.failWithErrorCode(
            new Error('XML parsing error'),
            Errors.XML_PARSING_ERROR
        );
    }

    return element as Element;
};

const parse = (
    xml: string | Document,
    options: Partial<ParseOptions> = {}
): VAST => {
    const parseOptions: ParseOptions = Object.assign(
        {},
        DEFAULT_OPTIONS,
        options
    );
    parseOptions.errorHandler = new ErrorHandler(parseOptions.strict);

    const element = toElement(xml, parseOptions);
    const unmarshaler = new Unmarshaler(schema);

    try {
        const root = unmarshaler.unmarshal(element);
        return createVAST(root, parseOptions);
    } catch (error) {
        parseOptions.errorHandler.fail(error as Error);
        throw error;
    }
};

export default parse;
